import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatFiat, formatCrypto, formatPct, FIATS, CRYPTO_SYMBOLS } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { TRADE } from "@/constants/testIds";

export default function Trade() {
  const location = useLocation();
  const { t } = useI18n();
  const initialAsset = location.state?.asset || "BTC";
  const initialSide = location.state?.side || "buy";
  const [asset, setAsset] = useState(initialAsset);
  const [fiat, setFiat] = useState("USD");
  const [side, setSide] = useState(initialSide);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [fxRates, setFxRates] = useState({ USD: 1 });

  const load = useCallback(async () => {
    const [w, m, fx] = await Promise.all([
      api.get("/wallet"),
      api.get("/markets"),
      api.get("/fx-rates"),
    ]);
    setWallet(w.data);
    setMarkets(m.data.assets);
    setFxRates(fx.data.rates);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const market = markets.find((m) => m.symbol === asset);
  const price = market?.price_usd || 0;
  const priceInFiat = price / (fxRates[fiat] || 1);

  const fiatBal = wallet?.balances.find((b) => b.currency === fiat)?.balance || 0;
  const cryptoBal = wallet?.balances.find((b) => b.currency === asset)?.balance || 0;

  const previewReceived = useMemo(() => {
    const n = Number(amount);
    if (!n || !price) return 0;
    const fee = 0.005;
    if (side === "buy") return (n * (1 - fee)) / priceInFiat;
    return n * priceInFiat * (1 - fee);
  }, [amount, side, price, priceInFiat]);

  const submit = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!n || n <= 0) return toast.error(t("trade.enter_amount"));
    setBusy(true);
    try {
      const body = { side, asset, fiat };
      if (side === "buy") body.amount_fiat = n;
      else body.amount_crypto = n;
      const r = await api.post("/wallet/trade", body);
      toast.success(`${side === "buy" ? t("trade.bought") : t("trade.sold")} ${asset}`);
      setAmount("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("trade.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("trade.eyebrow")}</div>
        <h1 className="font-display text-3xl font-bold mt-1">{t("trade.title")}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trade form */}
        <div className="lg:col-span-2 border border-[#2A2E38] bg-[#141720] p-6 md:p-8">
          <div className="flex border border-[#2A2E38] rounded-sm overflow-hidden mb-6 w-fit">
            <button
              data-testid={TRADE.sideBuy}
              type="button"
              onClick={() => setSide("buy")}
              className={`px-6 py-2 text-sm transition-colors ${side === "buy" ? "bg-[#7C9C69] text-white" : "bg-transparent text-[#A3A9B8] hover:text-[#F4F1EB]"}`}
            >
              {t("trade.buy")}
            </button>
            <button
              data-testid={TRADE.sideSell}
              type="button"
              onClick={() => setSide("sell")}
              className={`px-6 py-2 text-sm transition-colors ${side === "sell" ? "bg-[#C75C5C] text-white" : "bg-transparent text-[#A3A9B8] hover:text-[#F4F1EB]"}`}
            >
              {t("trade.sell")}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">{t("trade.crypto_asset")}</label>
                <select
                  data-testid={TRADE.assetSelect}
                  value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none"
                >
                  {CRYPTO_SYMBOLS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">{t("trade.fiat_currency")}</label>
                <select
                  data-testid={TRADE.fiatSelect}
                  value={fiat}
                  onChange={(e) => setFiat(e.target.value)}
                  className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none"
                >
                  {FIATS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">
                  {t("trade.amount_in")} {side === "buy" ? fiat : asset}
                </label>
                <span className="text-xs text-[#6E7587] font-mono">
                  {t("trade.available")} {side === "buy"
                    ? formatFiat(fiatBal, fiat)
                    : `${formatCrypto(cryptoBal)} ${asset}`}
                </span>
              </div>
              <input
                data-testid={TRADE.amountInput}
                type="number"
                step="any"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-4 font-mono text-2xl focus:border-[#7C5CFF] focus:outline-none"
              />
            </div>

            <div className="border border-[#2A2E38] p-4 rounded-sm bg-[#0B0D12] space-y-2 text-sm">
              <div className="flex justify-between text-[#A3A9B8]">
                <span>{t("trade.price")}</span>
                <span className="font-mono">{formatFiat(priceInFiat, fiat, 2)} / {asset}</span>
              </div>
              <div className="flex justify-between text-[#A3A9B8]">
                <span>{t("trade.fee")}</span>
                <span className="font-mono">0.50%</span>
              </div>
              <div className="flex justify-between border-t border-[#2A2E38] pt-2 text-[#F4F1EB]">
                <span className="text-[#A3A9B8]">{t("trade.you_receive")}</span>
                <span className="font-mono font-medium">
                  {side === "buy"
                    ? `${formatCrypto(previewReceived)} ${asset}`
                    : formatFiat(previewReceived, fiat, 2)}
                </span>
              </div>
            </div>

            <button
              data-testid={TRADE.submit}
              type="submit"
              disabled={busy || !amount}
              className={`w-full py-3 rounded-sm text-white transition-colors disabled:opacity-50 ${
                side === "buy" ? "bg-[#7C9C69] hover:bg-[#8FAF7B]" : "bg-[#C75C5C] hover:bg-[#D26F6F]"
              }`}
            >
              {busy ? t("trade.submitting") : `${side === "buy" ? t("trade.buy") : t("trade.sell")} ${asset}`}
            </button>
          </form>
        </div>

        {/* Asset info */}
        <div className="border border-[#2A2E38] bg-[#141720] p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">{t("trade.selected")}</div>
          <div className="mt-2 flex items-center gap-3">
            {market?.image && <img src={market.image} alt="" className="w-10 h-10" />}
            <div>
              <div className="font-display text-xl font-bold">{market?.name || asset}</div>
              <div className="text-xs text-[#6E7587] font-mono">{asset}</div>
            </div>
          </div>
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">{t("trade.live_price")} · {fiat}</div>
            <div className="font-mono text-3xl mt-1">{formatFiat(priceInFiat, fiat)}</div>
            <div className={`mt-1 text-sm font-mono ${market?.change_24h >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}>
              {formatPct(market?.change_24h)}
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[#2A2E38] space-y-3 text-sm">
            <div className="flex justify-between text-[#A3A9B8]">
              <span>{t("trade.your")} {asset}</span>
              <span className="font-mono">{formatCrypto(cryptoBal)}</span>
            </div>
            <div className="flex justify-between text-[#A3A9B8]">
              <span>{t("trade.your")} {fiat}</span>
              <span className="font-mono">{formatFiat(fiatBal, fiat)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
